import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  Users, 
  ShoppingBag, 
  Package 
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Order {
  id: string;
  customer_name: string;
  total_price: number;
  status: string;
  created_at: string;
  production_cost?: number;
}

export function AdminDashboard({ orders }: { orders: Order[] }) {
  const [financialStats, setFinancialStats] = useState({ income: 0, expense: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      const { data } = await supabase.from('financial_records').select('type, amount');
      if (data) {
        const stats = data.reduce((acc, curr) => {
          if (curr.type === 'income') acc.income += curr.amount;
          else acc.expense += curr.amount;
          return acc;
        }, { income: 0, expense: 0 });
        setFinancialStats(stats);
      }
    };
    fetchStats();
  }, []);

  const orderRevenue = orders
    .filter(o => ['delivered', 'shipped', 'processing'].includes(o.status))
    .reduce((acc, curr) => acc + curr.total_price, 0);

  const totalIncome = orderRevenue + financialStats.income;
  const totalExpense = financialStats.expense + orders.reduce((acc, o) => acc + (o.production_cost || 0), 0);
  const netProfit = totalIncome - totalExpense;

  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const recentOrders = orders.slice(0, 5);

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-none shadow-sm bg-gradient-to-br from-pink-500 to-pink-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-100 text-xs font-bold uppercase tracking-wider">Saldo em Caixa</p>
                <h3 className="text-2xl font-black mt-1">{formatCurrency(netProfit)}</h3>
              </div>
              <div className="bg-white/20 p-3 rounded-2xl">
                <DollarSign className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Entradas Totais</p>
                <h3 className="text-2xl font-black mt-1 text-green-600">{formatCurrency(totalIncome)}</h3>
              </div>
              <div className="bg-green-50 p-3 rounded-2xl">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Saídas Totais</p>
                <h3 className="text-2xl font-black mt-1 text-red-500">{formatCurrency(totalExpense)}</h3>
              </div>
              <div className="bg-red-50 p-3 rounded-2xl">
                <TrendingUp className="h-5 w-5 text-red-500 rotate-180" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Pedidos Pendentes</p>
                <h3 className="text-2xl font-black mt-1 text-gray-900">{pendingOrders}</h3>
              </div>
              <div className="bg-yellow-50 p-3 rounded-2xl">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-pink-500" />
              Vendas Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentOrders.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">Nenhuma venda registrada ainda.</p>
              ) : (
                recentOrders.map(order => (
                  <div key={order.id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-pink-50 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-pink-500" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{order.customer_name}</p>
                        <p className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleDateString('pt-BR')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-gray-900">{formatCurrency(order.total_price)}</p>
                      <Badge variant="outline" className="text-[9px] h-4">
                        {order.status === 'delivered' ? 'Concluído' : 'Em andamento'}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-pink-500" />
              Resumo da Loja
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-xl shadow-sm">
                    <Package className="h-5 w-5 text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Total de Pedidos</span>
                </div>
                <span className="text-lg font-black text-gray-900">{orders.length}</span>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-xl shadow-sm">
                    <TrendingUp className="h-5 w-5 text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Ticket Médio</span>
                </div>
                <span className="text-lg font-black text-gray-900">
                  {orders.length > 0 ? formatCurrency(orderRevenue / orders.length) : "R$ 0,00"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
